import {SagaIterator} from 'redux-saga';
import {call, put, putResolve, takeEvery} from 'redux-saga/effects';

import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';

import {logWithSentry} from 'libs/sentry/sentry';

import {PaymentMethodsType} from 'types/PaymentMethodsType';

import {CreateTokenResultSuccessType} from 'types/paymentOSType';
import {ActionType} from 'types/reducers';

import {
    save,
    setCards,
    setError,
    setLoading,
} from '../../reducers/paymentStep/creditCard/creditCard';
import {sendPayerInfoToSaga} from '../../reducers/paymentStep/payerInfo/payerInfo';
import {getPaymentMethods} from '../../reducers/paymentStep/paymentMethods/paymentMethods';
import {savePayment} from '../../reducers/paymentStep/savePaymentData/savePaymentData';

type ErrorResponseType = {
    payload?: {
        response?: {
            data?: {
                message: string;
            };
        };
    };
};

/**
 * Handler for errors
 *
 * @param {ErrorResponseType} error - error
 */
export function* handleError(error: ErrorResponseType): SagaIterator {
    // @ts-expect-error
    yield call(logWithSentry, error, 'Checkout: error during saving payment method');

    const errorMessage = error?.payload?.response?.data?.message;

    if (errorMessage) {
        yield put(setError(errorMessage));
    } else {
        yield put(
            setError(
                "Sorry, we can't save your payment information, please use another payment method or try again later.",
            ),
        );
    }
}

/**
 * Save payment method and set payer information into store
 *
 * @param {ActionType<{token: string, shouldSave: boolean}>} action - credit card "save" action
 */
export function* savePaymentStep({
    payload: {cvv, shouldSave, token},
}: ActionType<{token: string; cvv: string; shouldSave: boolean}>): SagaIterator {
    try {
        const action = yield putResolve(
            savePayment({
                paymentInfo: {method: PAYMENTS.METHOD_CREDIT_CARD, save: shouldSave, token},
            }),
        );

        const {
            card: {
                cc_exp_month: month,
                cc_exp_year: year,
                cc_last4: preparedCardNumber,
                cc_vendor: typeCard,
            },
        } = action.payload.data;

        yield put(
            sendPayerInfoToSaga({
                cvv,
                method: PAYMENTS.METHOD_CREDIT_CARD,
                month,
                preparedCardNumber,
                token,
                typeCard,
                year,
            }),
        );
    } catch (error) {
        yield put(setLoading(false));
    }
}

/**
 * Put saved user saved cards into store
 *
 * @param {ActionType<{data: PaymentMethodsType['list']}>} action - payment "getMethods" action
 */
export function* setSavedCards({
    payload,
}: ActionType<{data: PaymentMethodsType['list']}>): SagaIterator {
    const methodDetails = payload.data.ms_paymentsos;
    if (methodDetails) {
        const cards = (methodDetails.cards || []).map((card: CreateTokenResultSuccessType) => ({
            cardType: card.card_type,
            expirationDate: card.expiration_date,
            lastDigits: card.last_4_digits,
            token: card.token,
            vendor: card.vendor,
        }));
        yield put(setCards(cards));
    }
}

/**
 * Root saga for credit card method on checkout
 */
export function* creditCardPayment(): SagaIterator {
    yield takeEvery(`${getPaymentMethods.fulfilled}`, setSavedCards);
    yield takeEvery(`${save}`, savePaymentStep);
    // @ts-expect-error
    yield takeEvery(`${savePayment.rejected}`, handleError);
}
